<template>
  <div class="flex1 user-check-page">
    <!-- Search bar row -->
    <div class="search-bar">
      <div class="search-input-group">
        <label>User ID</label>
        <div class="search-row">
          <input class="form-control" v-model="userId" placeholder="Search by User ID" @keyup.enter="fetchUserData">
          <button class="btn btn-primary search-btn" @click="fetchUserData">
            <i class="fa fa-search"></i>
          </button>
        </div>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>
    </div>

    <div class="split-layout">
      <!-- Left column: Experiments + Audiences -->
      <div class="split-left">
        <div class="section">
          <h3 class="section-title">User's Active Experiments</h3>
          <div v-if="activeExperiment" class="experiment-section">
            <div class="card-grid">
              <CommonCard :title="activeExperiment.id"
                          :content="'Variant: ' + activeExperiment.variant"
                          date="Active"
                          :onClick="() => viewExperiment(activeExperiment.id)">
              </CommonCard>
            </div>
            <button class="btn btn-sm btn-danger remove-btn" @click="confirmRemoveExperiment">
              <i class="fa fa-times"></i> Remove User From This Experiment
            </button>
          </div>
          <div v-else-if="loadingData" class="loading-state">
            <i class="fa fa-cog fa-spin fa-2x fa-fw"></i>
          </div>
          <p v-else class="empty-text">No active experiment</p>
        </div>

        <div class="section">
          <h3 class="section-title">User's Audiences</h3>
          <div v-if="filteredAudiences.length > 0" class="card-grid">
            <CommonCard v-for="audience in filteredAudiences"
                        :key="audience.id"
                        :title="audience.name"
                        :content="audience.id"
                        :date="dateFormatter(audience.created_at)"
                        :onClick="() => viewAudience(audience.id)">
            </CommonCard>
          </div>
          <div v-else-if="loadingData" class="loading-state">
            <i class="fa fa-cog fa-spin fa-2x fa-fw"></i>
          </div>
          <p v-else class="empty-text">No records to show</p>
        </div>
      </div>

      <!-- Right column: Config Response -->
      <div class="split-right">
        <div class="section">
          <h3 class="section-title">Config Response</h3>
          <div v-if="loadingData" class="loading-state">
            <i class="fa fa-cog fa-spin fa-2x fa-fw"></i>
          </div>
          <div v-else-if="configPayload" class="json-viewer-wrapper">
            <pre class="json-viewer">{{ configPayload }}</pre>
            <button class="btn btn-primary btn-open-editor" @click="openJsonEditor">
              <i class="fa fa-external-link"></i> Open in JSON Editor View
            </button>
          </div>
          <p v-else class="empty-text">Search for a user to see their config response.</p>
        </div>
      </div>
    </div>

    <!-- JSON Editor Modal -->
    <JsonModal
      v-if="showJsonEditor"
      :valueObject="configPayload"
      :directOpen="true"
      :viewOnly="true"
      :editMode="false"
      title="Config Response"
      @modal-closed="closeJsonEditor"
    />

    <!-- Remove Experiment Confirmation Modal -->
    <div v-if="showRemoveConfirm" class="modal-overlay" @click="closeRemoveModal">
      <div class="modal-content" @click.stop>
        <h3>Remove User From Experiment</h3>
        <p>Are you sure you want to remove user <strong>{{ userId }}</strong> from experiment <strong>{{ activeExperiment ? activeExperiment.id : '' }}</strong>?</p>
        <div v-if="removeWarningMessage" class="remove-warning-box">
          <i class="fa fa-exclamation-triangle"></i>
          <span>{{ removeWarningMessage }}</span>
        </div>
        <div class="modal-actions">
          <button v-if="!removeWarningMessage" class="btn btn-danger" @click="removeExperiment" :disabled="isRemoving">
            <span v-if="isRemoving">Removing...</span>
            <span v-else>Confirm Remove</span>
          </button>
          <button class="btn btn-secondary" @click="closeRemoveModal">{{ removeWarningMessage ? 'Close' : 'Cancel' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatters from "../util/formatters.js";
import CommonCard from "../components/cards/CommonCard.vue";
import JsonModal from "../components/common/JsonModal.vue";
import { configService } from "../services/config.service.js";
import { managerService } from "../services/manager.service.js";
import notify from "../util/notification";

export default {
  data() {
    return {
      dateFormatter: formatters.dateFormatter,
      errorMessage: undefined,
      loadingData: false,
      userId: this.$route.query.id ? this.$route.query.id : "",
      activeExperiment: null,
      userAudiences: [],
      configPayload: null,
      showJsonEditor: false,
      showRemoveConfirm: false,
      isRemoving: false,
      removeWarningMessage: null
    };
  },
  computed: {
    filteredAudiences() {
      const audiences = this.$store.getters.audiences;
      return audiences.filter((audience) => this.userAudiences.includes(audience.id));
    }
  },
  methods: {
    fetchUserData() {
      if (!this.userId) {
        this.errorMessage = "User ID cannot be empty, please give an existing user id for active app";
        return;
      }
      this.loadingData = true;
      this.errorMessage = undefined;

      // Reset state when searching a new user
      this.activeExperiment = null;
      this.userAudiences = [];
      this.configPayload = null;
      this.showJsonEditor = false;

      const appId = this.$store.getters.activeApp;
      configService.getUserCheck(this.userId, appId)
        .then((res) => {
          const data = res.data.data;
          this.activeExperiment = data.activeExperiment || null;
          this.userAudiences = data.audiences || [];
          this.configPayload = JSON.stringify(data.parameters, null, 2);
          this.loadingData = false;
          this.saveCache();
        })
        .catch((err) => {
          console.log(`Error occurred during fetching user data with id: ${this.userId}\n Error:`, err);
          this.errorMessage = err.response?.data?.message || "An error occurred while fetching user data";
          this.loadingData = false;
        });
    },

    openJsonEditor() {
      this.showJsonEditor = true;
    },
    closeJsonEditor() {
      this.showJsonEditor = false;
    },
    viewExperiment(id) {
      const aid = this.$route.params.aid || this.$store.getters.activeApp;
      this.$router.push({ name: "Experiment", params: { aid, id } });
    },
    viewAudience(id) {
      const aid = this.$route.params.aid || this.$store.getters.activeApp;
      this.$router.push({ name: "Audience", params: { aid, id } });
    },
    confirmRemoveExperiment() {
      this.removeWarningMessage = null;
      this.showRemoveConfirm = true;
    },
    closeRemoveModal() {
      this.showRemoveConfirm = false;
      this.removeWarningMessage = null;
    },
    async removeExperiment() {
      this.isRemoving = true;
      try {
        await managerService.removeUserFromExperiment(this.userId, this.activeExperiment.id);
        notify('User successfully removed from experiment', 'success');
        this.showRemoveConfirm = false;
        this.fetchUserData();
      } catch (err) {
        if (err.response?.status === 409) {
          this.removeWarningMessage = err.response.data.message;
        } else {
          notify(err.response?.data?.message || 'Failed to remove user from experiment', 'error');
          this.showRemoveConfirm = false;
        }
      } finally {
        this.isRemoving = false;
      }
    },
    saveCache() {
      this.$store.commit('SET_USER_CHECK_CACHE', {
        userId: this.userId,
        activeExperiment: this.activeExperiment,
        userAudiences: this.userAudiences,
        configPayload: this.configPayload
      });
    },
    restoreCache() {
      const cache = this.$store.getters.userCheckCache;
      if (cache) {
        this.userId = cache.userId;
        this.activeExperiment = cache.activeExperiment;
        this.userAudiences = cache.userAudiences;
        this.configPayload = cache.configPayload;
      }
    }
  },
  created() {
    this.restoreCache();
  },
  components: {
    CommonCard,
    JsonModal
  },
  filters: {
    addUTCAfter(value) {
      if (value) {
        return value + " UTC";
      } else {
        return "-";
      }
    },
  },
};
</script>

<style scoped>
.user-check-page {
  overflow: hidden;
}

/* Search bar */
.search-bar {
  margin-bottom: 1.5rem;
}

.search-input-group {
  max-width: 400px;
}

.search-input-group label {
  font-weight: 600;
  margin-bottom: 0.4rem;
}

.search-row {
  display: flex;
  gap: 8px;
}

.search-row .form-control {
  flex: 1;
}

.search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
}

.error-text {
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.3rem;
  margin-bottom: 0;
}

/* Two-column split — no Bootstrap row to avoid negative margin overflow */
.split-layout {
  display: flex;
  gap: 24px;
}

.split-left {
  flex: 7;
  min-width: 0;
  overflow: hidden;
}

.split-right {
  flex: 5;
  min-width: 0;
  overflow: hidden;
}

/* Sections */
.section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

/* Card grid — cards flow in rows, constrained to container */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

/* Override CommonCard's min-width: 300px that causes overflow */
.card-grid >>> .card {
  min-width: 0 !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

/* States */
.loading-state {
  padding: 2rem 0;
  text-align: center;
  color: #888;
}

.empty-text {
  color: #888;
  font-size: 0.9rem;
}

/* JSON viewer */
.json-viewer-wrapper {
  margin-top: 0.5rem;
}

.json-viewer {
  background-color: #1a2332;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #3d5875;
  max-height: calc(100vh - 300px);
  overflow: auto;
  white-space: pre;
  word-break: normal;
  overflow-wrap: normal;
}

.btn-open-editor {
  margin-top: 10px;
  width: 100%;
}

/* Remove experiment button */
.remove-btn {
  margin-top: 10px;
}

/* Remove experiment confirmation modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #25253a;
  color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  padding: 30px 20px;
  max-width: 600px;
  width: 90%;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.remove-warning-box {
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #ffc107;
  font-size: 0.9rem;
  line-height: 1.4;
}

.remove-warning-box i {
  margin-top: 2px;
  flex-shrink: 0;
}
</style>
